import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowUp, CalendarClock, Search } from 'lucide-react'
import { SCHEDULE_TYPES } from '@acs/shared'
import type { Page, ScheduleRow, ScheduleType } from '@acs/shared'
import { Badge } from '@/components/ui/badge'
import type { Tone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip } from '@/components/ui/tooltip'
import { ErrorPanel } from '@/components/shell/error-boundary'
import { useSetPageCount } from '@/components/shell/topbar'
import { apiGet } from '@/lib/api'
import { useUiStore } from '@/stores/ui-store'
import { cn } from '@/lib/cn'

/**
 * Schedule checker.
 *
 * The four labor-demand counts are the reason this table exists, and they are
 * not equally interesting: `soft match` is the number an operator watches,
 * because it is the one that moves when Amazon opens capacity that a customer
 * can actually be placed into. So it gets weight, a tone and a column border;
 * the other three are present for context and stay quiet.
 *
 * Two things were wrong here and are fixed below.
 *
 *  - **The roles had no owner.** Rows and cells were announced with
 *    `role="row"` / `role="cell"` inside a plain `<div>`, so a screen reader
 *    saw orphaned table parts and read them as generic groups — the row/column
 *    relationship, the one thing those roles exist to express, never reached
 *    the user. The scroll body now carries `role="table"` with `aria-colcount`
 *    and `aria-rowcount`, and every row carries its **absolute**
 *    `aria-rowindex`: under virtualisation only a window of rows is in the DOM,
 *    so an index derived from the rendered list would announce "row 3 of 111"
 *    for whatever happened to be third on screen.
 *  - **It was a 1180px table on a 360px phone.** Below `md` each row becomes a
 *    four-line card built from the very same cells, so the accessibility tree
 *    is identical at every width and the document still never scrolls
 *    sideways.
 */

/** 111 rows live today. One request covers the table with room to spare. */
const PAGE_LIMIT = 200

/** Above this, the DOM cost of a full render starts showing up in scrolling. */
const VIRTUALIZE_ABOVE = 100

const SEARCH_DEBOUNCE_MS = 250

/** Schedule, Location, Site, Shift, Type, Avail, Hard, Soft, Open, Updated. */
const COLUMN_COUNT = 10

type TypeFilter = 'ALL' | ScheduleType
type SortDir = 'asc' | 'desc'

const TYPE_LABELS: Record<ScheduleType, string> = {
  FULL_TIME: 'Full time',
  PART_TIME: 'Part time',
  FLEX_TIME: 'Flex',
  REDUCED_TIME: 'Reduced',
}

/**
 * Tone carries meaning, not decoration: full-time is the placement everyone
 * wants, reduced-time is the one that usually disappoints.
 */
const TYPE_TONES: Record<ScheduleType, Tone> = {
  FULL_TIME: 'accent',
  PART_TIME: 'info',
  FLEX_TIME: 'neutral',
  REDUCED_TIME: 'warn',
}

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  ...SCHEDULE_TYPES.map((type) => ({ value: type as TypeFilter, label: TYPE_LABELS[type] })),
]

const NUM = new Intl.NumberFormat('en-US')

/* ------------------------------------------------------------------ *
 * Viewport
 *
 * Below `md` the row is a card, above it a table line. That changes the row
 * *height*, which the virtualizer measures against, so it has to be a real
 * value in render rather than a media query buried in CSS.
 * ------------------------------------------------------------------ */

const CARD_LAYOUT = '(max-width: 767.98px)'

function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => {
        mql.removeEventListener('change', onChange)
      }
    },
    [query]
  )

  const getSnapshot = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  }, [query])

  return React.useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/* ------------------------------------------------------------------ *
 * Layout
 *
 * One template shared by the header and every row, so the two can never drift.
 * Below `md` the same ten cells are re-placed into a four-column, four-line
 * card by `PLACE` — no cell is added or removed, which is what keeps
 * `aria-colcount` honest at every size (see MID_TIER_HIDDEN for the one
 * deliberate exception in the middle tier).
 * ------------------------------------------------------------------ */

const HEADER_ROW =
  'grid grid-cols-[minmax(160px,0.9fr)_minmax(170px,1.1fr)_minmax(180px,1.35fr)_repeat(4,minmax(66px,74px))_minmax(96px,0.55fr)] ' +
  'min-[1180px]:grid-cols-[minmax(160px,0.9fr)_minmax(170px,1.1fr)_minmax(118px,0.6fr)_minmax(180px,1.35fr)_112px_repeat(4,minmax(66px,74px))_minmax(96px,0.55fr)]'

const BODY_ROW =
  'grid grid-cols-4 content-center items-center gap-x-3 gap-y-0.5 overflow-hidden px-3 py-2 ' +
  'md:grid-cols-[minmax(160px,0.9fr)_minmax(170px,1.1fr)_minmax(180px,1.35fr)_repeat(4,minmax(66px,74px))_minmax(96px,0.55fr)] ' +
  'md:content-normal md:items-stretch md:gap-0 md:px-0 md:py-0 ' +
  'min-[1180px]:grid-cols-[minmax(160px,0.9fr)_minmax(170px,1.1fr)_minmax(118px,0.6fr)_minmax(180px,1.35fr)_112px_repeat(4,minmax(66px,74px))_minmax(96px,0.55fr)]'

/**
 * The middle table tier — `md` to 1180 — runs WITHOUT the Site and Type
 * columns. The full ten columns have an 1100px floor, so the entire iPad range
 * (768-1194) would otherwise scroll sideways forever; dropping these two (the
 * most static cells on the row — the counts are what an operator watches, the
 * ids are what identify the row) brings the floor to 870, which fits every
 * landscape tablet. Both stay in the card below `md` and return at 1180.
 * `aria-colcount` stays at ten across the middle tier — a known, deliberate
 * drift, cheaper than a colcount that changes with the window.
 */
const MID_TIER_HIDDEN = 'md:hidden min-[1180px]:flex'

/** Card placement below `md`; `md:col-auto md:row-auto` hands flow back over. */
const PLACE = {
  schedule: 'col-span-2 row-start-2 md:col-auto md:row-auto',
  location: 'col-span-3 row-start-1 md:col-auto md:row-auto',
  site: 'col-span-2 col-start-3 row-start-2 md:col-auto md:row-auto',
  shift: 'col-span-2 row-start-3 md:col-auto md:row-auto',
  type: 'col-start-4 row-start-1 md:col-auto md:row-auto',
  avail: 'col-start-1 row-start-4 md:col-auto md:row-auto',
  hard: 'col-start-2 row-start-4 md:col-auto md:row-auto',
  soft: 'col-start-3 row-start-4 md:col-auto md:row-auto',
  open: 'col-start-4 row-start-4 md:col-auto md:row-auto',
  updated: 'col-span-2 col-start-3 row-start-3 md:col-auto md:row-auto',
} as const

/** The card's gutters live on the row, so cell padding starts at `md`. */
const CELL = 'flex min-w-0 items-center md:px-3'

/**
 * The id cell's hover: both UUIDs and how old the answer is.
 *
 * The age matters more than it looks. These are filled in by the schedule
 * poller, not by this console, so "resolved 6 minutes ago" and "resolved
 * yesterday" are different levels of confidence about a value a claim is about
 * to be sent with — and when a shift has no UUID, how long it has been that way
 * is the difference between waiting and reassigning.
 */
function uuidTitle(row: ScheduleRow): string {
  if (row.scheduleUuidGaveUpAtMs !== null) {
    return `${row.scheduleId}\nThe schedule poller gave up resolving this shift's UUID — it is not coming.`
  }
  if (row.scheduleUuid === null) {
    return `${row.scheduleId}\nNo shift UUID yet. The schedule poller fills these in on its own cadence.${
      row.jobUuid === null ? '' : `\nJob UUID: ${row.jobUuid}`
    }`
  }
  const mins = row.scheduleUuidAtMs === null ? null : Math.round((Date.now() - row.scheduleUuidAtMs) / 60_000)
  return [
    `Shift UUID: ${row.scheduleUuid}`,
    row.jobUuid === null ? null : `Job UUID: ${row.jobUuid}`,
    mins === null ? null : `Resolved ${mins} min ago`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n')
}

/**
 * Row heights. The table line is one line; the card is four, and the tallest
 * of them stacks a 12.5px id over an 11px sub-id — about 118px of content once
 * the gutters are counted, so neither card height clips a descender.
 */
function rowHeightFor(card: boolean, compact: boolean): number {
  if (card) return compact ? 120 : 132
  return compact ? 44 : 52
}

/* ------------------------------------------------------------------ *
 * Query
 * ------------------------------------------------------------------ */

interface SchedulesQuery {
  q: string
  scheduleType: TypeFilter
}

function schedulesKey(query: SchedulesQuery): readonly unknown[] {
  return ['schedules', { q: query.q, scheduleType: query.scheduleType, limit: PAGE_LIMIT }] as const
}

function useSchedules(query: SchedulesQuery) {
  return useQuery({
    queryKey: schedulesKey(query),
    queryFn: ({ signal }) =>
      apiGet<Page<ScheduleRow>>(
        '/api/schedules',
        {
          q: query.q === '' ? undefined : query.q,
          scheduleType: query.scheduleType === 'ALL' ? undefined : query.scheduleType,
          limit: PAGE_LIMIT,
          offset: 0,
        },
        signal
      ),
    // Schedule capacity changes on Amazon's clock, not ours, and nothing in
    // the SSE stream patches this table — so unlike the customer grid, this
    // one does poll.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

function HeadCell({
  children,
  className,
  align = 'left',
  title,
}: {
  children: React.ReactNode
  className?: string
  align?: 'left' | 'right'
  title?: string
}) {
  // Deliberately not built on `CELL`: that carries `md:px-3`, and a variant
  // utility survives an unprefixed `p-0` override from a caller — the sort
  // header needs its button to fill the cell edge to edge.
  const content = (
    <div
      role="columnheader"
      className={cn(
        'flex h-9 min-w-0 items-center px-3',
        'font-mono text-[10.5px] tracking-[0.11em] text-faint uppercase',
        align === 'right' && 'justify-end',
        className
      )}
    >
      <span className="truncate">{children}</span>
    </div>
  )

  return title ? (
    <Tooltip content={title} side="bottom">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

/* ------------------------------------------------------------------ *
 * Row
 * ------------------------------------------------------------------ */

function CountCell({
  value,
  place,
  label,
  short,
  emphasis = false,
}: {
  value: number
  place: string
  /** Read by assistive tech, and the only label the card has. */
  label: string
  /** Three or four characters, shown inline on the card only. */
  short: string
  emphasis?: boolean
}) {
  return (
    <div
      role="cell"
      aria-label={`${label} ${value}`}
      className={cn(
        CELL,
        place,
        'justify-end gap-1',
        // A vertical rule that separates two columns means nothing once the
        // columns are stacked, so the border is a table-only affordance.
        emphasis && 'md:border-l md:border-line/70'
      )}
    >
      <span className="font-mono text-[9.5px] tracking-[0.08em] text-faint uppercase md:hidden">
        {short}
      </span>
      <span
        className={cn(
          'font-mono tabular',
          emphasis
            ? cn('text-[13px] font-semibold', value > 0 ? 'text-live' : 'text-faint')
            : cn('text-[12.5px]', value > 0 ? 'text-muted' : 'text-faint')
        )}
      >
        {NUM.format(value)}
      </span>
    </div>
  )
}

function ScheduleRowView({
  row,
  rowIndex,
  rowHeight,
  style,
}: {
  row: ScheduleRow
  /** Absolute, 1-based, header included. Never the index within the window. */
  rowIndex: number
  rowHeight: number
  style?: React.CSSProperties
}) {
  const updated = React.useMemo(() => {
    const ms = row.updatedAtMs
    if (!Number.isFinite(ms) || ms <= 0) return '—'
    return new Date(ms).toLocaleString(undefined, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }, [row.updatedAtMs])

  return (
    <div
      role="row"
      aria-rowindex={rowIndex}
      style={{ ...style, height: rowHeight }}
      className={cn(BODY_ROW, 'border-b border-line/70 hover:bg-s3/60')}
    >
      {/*
        The UUID state rides on the id cell rather than becoming an eleventh
        column: this grid already drops two columns to fit a smaller screen, and
        a whole column for one boolean would cost that layout for very little.

        The chip marks the SCARCE state — resolved — because most shifts do not
        have a UUID yet, and marking the common one would put a badge on nearly
        every row. The hover carries the detail.
      */}
      <div
        role="cell"
        title={uuidTitle(row)}
        className={cn(CELL, PLACE.schedule, 'flex-col items-start justify-center gap-px')}
      >
        <span className="flex max-w-full items-baseline gap-1">
          <span className="max-w-full truncate font-mono text-[12.5px] text-fg">
            {row.scheduleId}
          </span>
          {row.scheduleUuid !== null ? (
            <span className="shrink-0 rounded-sm bg-s3 px-1 text-[9px] font-medium tracking-wide text-muted">
              UUID
            </span>
          ) : null}
        </span>
        <span className="max-w-full truncate font-mono text-[11px] text-faint">{row.jobId}</span>
      </div>

      <div
        role="cell"
        className={cn(CELL, PLACE.location, 'flex-col items-start justify-center gap-px')}
      >
        <span className="max-w-full truncate text-[13px] text-fg">
          {row.city}
          {row.state ? <span className="text-muted">, {row.state}</span> : null}
        </span>
        {/* The street address is the first thing to go on a phone — it is the
            longest string in the row and the least useful for a spot check. */}
        {row.address ? (
          <span className="hidden max-w-full truncate text-[11px] text-faint md:block">
            {row.address}
          </span>
        ) : null}
      </div>

      <div
        role="cell"
        className={cn(
          CELL,
          PLACE.site,
          MID_TIER_HIDDEN,
          'flex-col items-end justify-center gap-px md:items-start'
        )}
      >
        <span className="max-w-full truncate font-mono text-[12.5px] text-fg">{row.siteId}</span>
        {row.alpsCode ? (
          <span className="max-w-full truncate font-mono text-[11px] text-faint">
            {row.alpsCode}
          </span>
        ) : null}
      </div>

      <div role="cell" className={cn(CELL, PLACE.shift)}>
        <Tooltip content={row.scheduleText} side="top">
          <span className="truncate text-[12.5px] text-muted">{row.scheduleText || '—'}</span>
        </Tooltip>
      </div>

      <div role="cell" className={cn(CELL, PLACE.type, MID_TIER_HIDDEN, 'justify-end md:justify-start')}>
        <Badge tone={TYPE_TONES[row.scheduleType]} dot>
          {TYPE_LABELS[row.scheduleType]}
        </Badge>
      </div>

      <CountCell
        value={row.laborDemandAvailableCount}
        place={PLACE.avail}
        label="Available"
        short="avl"
      />
      <CountCell
        value={row.laborDemandHardMatchCount}
        place={PLACE.hard}
        label="Hard match"
        short="hard"
      />
      <CountCell
        value={row.laborDemandSoftMatchCount}
        place={PLACE.soft}
        label="Soft match"
        short="soft"
        emphasis
      />
      <CountCell value={row.laborDemandOpenCount} place={PLACE.open} label="Open" short="open" />

      <div role="cell" className={cn(CELL, PLACE.updated, 'justify-end')}>
        <span className="truncate font-mono text-[11.5px] text-faint tabular">{updated}</span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Loading
 * ------------------------------------------------------------------ */

/**
 * Same DOM order as a real row — a cell's position in its row is what gives it
 * a column, so a placeholder that reorders them would announce the wrong one.
 * Where each cell *appears* is `place`'s job, not the order's.
 */
const SKELETON_CELLS: { place: string; width: string; align?: string }[] = [
  { place: PLACE.schedule, width: 'w-28' },
  { place: PLACE.location, width: 'w-32' },
  { place: PLACE.site, width: 'w-20', align: 'justify-end md:justify-start' },
  { place: PLACE.shift, width: 'w-36' },
  { place: PLACE.type, width: 'w-16 rounded-full', align: 'justify-end md:justify-start' },
  { place: PLACE.avail, width: 'w-6', align: 'justify-end' },
  { place: PLACE.hard, width: 'w-6', align: 'justify-end' },
  { place: PLACE.soft, width: 'w-6', align: 'justify-end' },
  { place: PLACE.open, width: 'w-6', align: 'justify-end' },
  { place: PLACE.updated, width: 'w-16', align: 'justify-end' },
]

function SkeletonRow({ rowIndex, rowHeight }: { rowIndex: number; rowHeight: number }) {
  return (
    <div
      role="row"
      aria-rowindex={rowIndex}
      style={{ height: rowHeight }}
      className={cn(BODY_ROW, 'border-b border-line/70')}
    >
      {SKELETON_CELLS.map((cell) => (
        <div key={cell.place} role="cell" className={cn(CELL, cell.place, cell.align)}>
          <Skeleton className={cn('h-3', cell.width)} />
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export function SchedulesPage() {
  const density = useUiStore((s) => s.density)
  const card = useMediaQuery(CARD_LAYOUT)
  const rowHeight = rowHeightFor(card, density === 'compact')

  const [rawQuery, setRawQuery] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('ALL')
  const [sortDir, setSortDir] = React.useState<SortDir>('desc')

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebounced(rawQuery.trim())
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(handle)
    }
  }, [rawQuery])

  const schedules = useSchedules({ q: debounced, scheduleType: typeFilter })

  const rows = React.useMemo(() => {
    const source = schedules.data?.rows ?? []
    // Sorting is client-side because the whole result set is already here;
    // a round trip to reverse 111 rows would be theatre.
    return [...source].sort((a, b) =>
      sortDir === 'desc' ? b.updatedAtMs - a.updatedAtMs : a.updatedAtMs - b.updatedAtMs
    )
  }, [schedules.data?.rows, sortDir])

  const total = schedules.data?.total ?? null
  useSetPageCount(schedules.isSuccess ? (total ?? rows.length) : null)

  const truncated = total !== null && total > rows.length

  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const virtualize = rows.length > VIRTUALIZE_ABOVE

  const virtualizer = useVirtualizer({
    count: virtualize ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  })

  // `estimateSize` is captured when the virtualizer measures, so a density
  // change or a breakpoint crossing has to ask for a re-measure explicitly —
  // otherwise the rows move to their new height and the spacer keeps the old
  // one, leaving a gap at the bottom of the list.
  React.useEffect(() => {
    virtualizer.measure()
  }, [virtualizer, rowHeight])

  const sortLabel =
    sortDir === 'desc'
      ? 'Sorted newest first — click for oldest first'
      : 'Sorted oldest first — click for newest first'

  const toggleSort = React.useCallback(() => {
    setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
  }, [])

  /* The header row is a real row in the accessibility tree, but only from `md`
     up — the card layout has no column headers to show. So the offset that
     turns a list index into an `aria-rowindex` follows the layout. */
  const headerRows = card ? 0 : 1
  const ariaRowCount = schedules.isPending ? -1 : rows.length + headerRows

  const showEmpty = !schedules.isPending && !schedules.isError && rows.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Toolbar ------------------------------------------------------- */}
      <div className="flex flex-none flex-wrap items-center gap-2 border-b border-line bg-s1 px-3 py-2 md:gap-3">
        {/* Full width on a phone, a fixed field on a laptop. */}
        <div className="w-full min-w-0 md:w-[260px]">
          <Input
            value={rawQuery}
            onChange={(e) => {
              setRawQuery(e.target.value)
            }}
            onClear={() => {
              setRawQuery('')
            }}
            icon={<Search aria-hidden="true" />}
            placeholder="Search city, site, schedule id…"
            aria-label="Search schedules"
            spellCheck={false}
          />
        </div>

        <Segmented
          value={typeFilter}
          onChange={setTypeFilter}
          options={TYPE_OPTIONS}
          aria-label="Filter by schedule type"
        />

        {/* The column header owns this on a table; the card has no header, so
            the control moves into the toolbar. */}
        <Button
          variant="secondary"
          size="sm"
          className="md:hidden"
          aria-label={sortLabel}
          onClick={toggleSort}
        >
          {sortDir === 'desc' ? (
            <ArrowDown aria-hidden="true" />
          ) : (
            <ArrowUp aria-hidden="true" />
          )}
          Updated
        </Button>

        {truncated ? (
          <span className="font-mono text-[11.5px] text-warn tabular">
            showing {NUM.format(rows.length)} of {NUM.format(total)}
          </span>
        ) : null}

        <span className="ml-auto font-mono text-[11.5px] text-faint tabular">
          {schedules.isPending ? 'loading…' : `${NUM.format(rows.length)} rows`}
        </span>
      </div>

      {/* Table --------------------------------------------------------- */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto overscroll-contain">
        {schedules.isError ? (
          <ErrorPanel
            error={schedules.error}
            onRetry={() => {
              void schedules.refetch()
            }}
            title="Could not load schedules"
          />
        ) : (
          // The minimum width belongs to the table, not the page: this element
          // overflows its scroll parent horizontally and the document does
          // not. Below `md` there is no minimum at all, because there is no
          // table to keep aligned.
          <div className="md:min-w-[870px] min-[1180px]:min-w-[1100px]">
            <div
              role="table"
              aria-label="Schedules"
              aria-colcount={COLUMN_COUNT}
              aria-rowcount={ariaRowCount}
              aria-busy={schedules.isPending || undefined}
            >
              <div
                role="rowgroup"
                className={cn(
                  'hidden border-b border-line bg-s1/95 backdrop-blur-[2px]',
                  'md:sticky md:top-0 md:z-10 md:block'
                )}
              >
                <div role="row" aria-rowindex={1} className={HEADER_ROW}>
                  <HeadCell>Schedule</HeadCell>
                  <HeadCell>Location</HeadCell>
                  {/* The header only exists from `md` up, so `hidden` alone
                      covers the middle tier. */}
                  <HeadCell className="hidden min-[1180px]:flex">Site</HeadCell>
                  <HeadCell>Shift</HeadCell>
                  <HeadCell className="hidden min-[1180px]:flex">Type</HeadCell>
                  <HeadCell
                    align="right"
                    title="Total labor demand slots published for this schedule"
                  >
                    Avail
                  </HeadCell>
                  <HeadCell align="right" title="Candidates matching every hard requirement">
                    Hard
                  </HeadCell>
                  <HeadCell
                    align="right"
                    className="border-l border-line/70 text-live"
                    title="Soft matches — the number that moves when capacity opens up. This is the one to watch."
                  >
                    Soft
                  </HeadCell>
                  <HeadCell align="right" title="Slots still unfilled">
                    Open
                  </HeadCell>
                  <HeadCell align="right" className="p-0">
                    <Tooltip content={sortLabel} side="bottom">
                      <button
                        type="button"
                        onClick={toggleSort}
                        aria-label={sortLabel}
                        className={cn(
                          'flex h-9 w-full items-center justify-end gap-1 px-3',
                          'font-mono text-[10.5px] tracking-[0.11em] uppercase',
                          'text-accent transition-colors duration-100 hover:text-fg'
                        )}
                      >
                        Updated
                        {sortDir === 'desc' ? (
                          <ArrowDown aria-hidden="true" className="size-3" />
                        ) : (
                          <ArrowUp aria-hidden="true" className="size-3" />
                        )}
                      </button>
                    </Tooltip>
                  </HeadCell>
                </div>
              </div>

              {schedules.isPending ? (
                <div role="rowgroup">
                  {Array.from({ length: 12 }, (_, i) => (
                    <SkeletonRow key={i} rowIndex={i + 1 + headerRows} rowHeight={rowHeight} />
                  ))}
                </div>
              ) : virtualize ? (
                <div
                  role="rowgroup"
                  style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
                >
                  {virtualizer.getVirtualItems().map((item) => {
                    const row = rows[item.index]
                    if (!row) return null
                    return (
                      <ScheduleRowView
                        key={row.scheduleId}
                        row={row}
                        // Absolute: `item.index` is the position in `rows`,
                        // not in the rendered window.
                        rowIndex={item.index + 1 + headerRows}
                        rowHeight={rowHeight}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          transform: `translateY(${item.start}px)`,
                        }}
                      />
                    )
                  })}
                </div>
              ) : (
                <div role="rowgroup">
                  {rows.map((row, index) => (
                    <ScheduleRowView
                      key={row.scheduleId}
                      row={row}
                      rowIndex={index + 1 + headerRows}
                      rowHeight={rowHeight}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Outside the table: a role="table" may only contain rowgroups,
                and an empty state is not one. */}
            {showEmpty ? (
              <EmptyState
                icon={<CalendarClock />}
                title="No schedules match"
                description={
                  debounced === '' && typeFilter === 'ALL'
                    ? 'The schedule checker has not published any rows yet.'
                    : 'Nothing matches the current search and type filter.'
                }
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default SchedulesPage
